import { ReactNode } from 'react'

import { ColumnProps } from 'antd/lib/table'

import { Expense } from 'core/application/employeeDashboard/summary/reimbursementRequests/getExpenses'
import { ExpenseStatus } from 'core/domain/expenses/expenseStatus'

import Popover from 'common/components/dataDisplay/Popover'

import { parseZoneFormat } from 'common/dateFormatters'
import { formatMoney } from 'common/moneyFormatter'

import ExpenseStatusLabel from '../ExpenseStatusLabel'
import { mapExpenseStatusWithContent, mapExpenseStatusWithTitle } from './ExpenseStatusMapping'
import MoreActions from './MoreActions'

const getStatusIconContent = (
  { expenseStatus, messageToEmployee, isDuplicate }: Expense,
  translate: (key: string) => string,
  turnAroundTime?: string,
): ReactNode => {
  const expenseStatusIcon = (
    <>
      {' '}
      <ExpenseStatusLabel expenseStatus={expenseStatus} />
    </>
  )
  return expenseStatus === ExpenseStatus.ActionRequired ? (
    <Popover
      trigger="hover"
      content={<div dangerouslySetInnerHTML={{ __html: messageToEmployee }} />}
      placement="top"
      title={translate('reimbursement-requests.action-required-label')}
    >
      {expenseStatusIcon}
    </Popover>
  ) : expenseStatus === ExpenseStatus.Ineligible && isDuplicate ? (
    <Popover
      title={'Duplicate'}
      placement="top"
      trigger="hover"
      content={<div className="expense_popover_width">This is a duplicate</div>}
    >
      {expenseStatusIcon}
    </Popover>
  ) : (
    <Popover
      title={translate(mapExpenseStatusWithTitle.get(expenseStatus) ?? '')}
      placement="top"
      content={
        <div className="expense_popover_width">
          {translate(mapExpenseStatusWithContent.get(expenseStatus)).replace(
            '[TURN_AROUND_TIME]',
            turnAroundTime ?? '',
          ) ?? ''}
        </div>
      }
    >
      {expenseStatusIcon}
    </Popover>
  )
}

export const reimbursementRequestsColumns = (
  translate: (key: string) => string,
  onDelete: (expenseId: string) => void,
  onViewFiles: (expenseId: string) => void,
  turnAroundTimes?: string,
): ColumnProps<Expense>[] => [
  {
    title: translate('reimbursement-requests.expense-id-label'),
    dataIndex: 'expenseNumber',
    key: 'expenseNumber',
    className: 't-expenseNumber',
  },
  {
    title: translate('reimbursement-requests.service-provider-label'),
    dataIndex: 'serviceProvider',
    key: 'serviceProvider',
    className: 't-serviceProvider',
  },
  {
    title: translate('reimbursement-requests.submitted-on-label'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    sorter: true,
    sortDirections: ['descend', 'ascend'],
    render: (item: Date) => parseZoneFormat(item),
    className: 't-createdAt',
  },
  {
    title: translate('reimbursement-requests.service-date-label'),
    dataIndex: 'purchasedDate',
    key: 'PurchasedDate',
    render: (item: Date) => parseZoneFormat(item),
    sorter: true,
    sortDirections: ['descend', 'ascend'],
    className: 't-purchaseDate',
  },
  {
    title: translate('reimbursement-requests.status-label'),
    dataIndex: 'expenseStatus',
    key: 'expenseStatus',
    render: (_, record: Expense) => getStatusIconContent(record, translate, turnAroundTimes),
    className: 't-expenseStatus',
  },
  {
    title: translate('reimbursement-requests.expense-amount-label'),
    dataIndex: 'amount',
    key: 'amount',
    align: 'right',
    render: (item: number) => formatMoney(item),
    className: 't-amount',
  },
  {
    title: translate('reimbursement-requests.actions-label'),
    key: 'actions',
    align: 'center',
    render: (_, record: Expense) => (
      <MoreActions expense={record} onDelete={onDelete} onViewFiles={onViewFiles} translate={translate} />
    ),
    className: 't-actions',
  },
]
